import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Skeleton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { formatApiDate } from '../../../utils/dateUtils';
import ReportTabLayout from '../common/ReportTabLayout';
import ReportsPagination from '../common/ReportsPagination';
import ReportsEmptyState from '../common/ReportsEmptyState';
import ReportsErrorState from '../common/ReportsErrorState';
import ReportsFilterBar, { ICON_ONLY_ACTIONS_BELOW_PX } from '../common/ReportsFilterBar';
import ReportsSubTabs from '../common/ReportsSubTabs';
import CalendarTableSection from '../common/CalendarTableSection';
import useReportFilters from '../common/useReportFilters';
import { EXCHANGE_OPTIONS, PNL_FILTER_DEFS } from './pnlFilters';
import { formatPnlInr } from './pnlFormatters';
import PnlHistoryTable from './PnlHistoryTable';
import PnlHistoryMobileList from './PnlHistoryMobileList';
import { fetchBillDetailPnl, fetchPnlDaywise } from '../../../services/backendAPI';

// primusAPI rejects with the server's message string, or with undefined when
// no response came back at all (network down, timeout).
const toErrorMessage = (error, fallback) => (typeof error === 'string' && error) || error?.message || fallback;

// Gross P&L (before brokerage), for the day and for each exchange: the same
// figure as the bills' PNL, so a day's cell matches that day's table rows.
const entryPnl = (entry) => parseFloat(entry.gross_pnl) || 0;

// Calendar cells per day: the day's total, or the selected exchange's entry.
// Days where that exchange didn't trade, or whose P&L is 0 (e.g. brokerage
// only), get no cell and show as blank days.
const toDailyPnl = (days, exchange) => days.flatMap((day) => {
  const entry = exchange === 'ALL' ? day : (day.exchanges || []).find((e) => e.exchange === exchange);
  const pnl = entry ? entryPnl(entry) : 0;
  return pnl !== 0 ? [{ date: day.date, pnl }] : [];
});


const PnlHistoryDetails = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const iconOnlyActions = useMediaQuery(theme.breakpoints.between('sm', ICON_ONLY_ACTIONS_BELOW_PX));
  const { enqueueSnackbar } = useSnackbar();

  // Table data state
  const [pnlHistory, setPnlHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const filterProps = useReportFilters({
    defaultPreset: 'week',
    extraFilterDefs: PNL_FILTER_DEFS,
    onFiltersApplied: (filters) => {
      setPage(0);
      fetchPnlData(0, rowsPerPage, filters);
    },
  });

  const {
    startDate,
    endDate,
    selectedDay,
    handleDayClick,
    extraFilters,
    applyExtraFilter,
    handleResetFilters,
    getFilterState,
  } = filterProps;

  // Daily totals for the whole range — independent of table page and of the
  // selected day (the calendar always shows the full range).
  const [calendarDays, setCalendarDays] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState(false);

  // Only the newest fetch may update the table, so a slower older response can't
  // overwrite the rows of the exchange or page picked after it.
  const latestRequestIdRef = useRef(0);

  // `filters` is a full snapshot from useReportFilters; it defaults to the
  // current committed state for page / rows-per-page changes.
  const fetchPnlData = async (newPage, newRowsPerPage, filters = getFilterState()) => {
    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;
    const isSuperseded = () => requestId !== latestRequestIdRef.current;

    setLoading(true);
    setLoadError(null);
    try {
      const day = filters.selectedDay;
      const params = {
        start_date: formatApiDate(day || filters.startDate),
        end_date: formatApiDate(day || filters.endDate),
        page: newPage + 1,
        page_size: newRowsPerPage,
        exchange: filters.exchange === 'ALL' ? '' : filters.exchange,
      };

      const response = await fetchBillDetailPnl(params);
      if (isSuperseded()) return;
      // An empty page can arrive as `data: null` (Go's empty list) with
      // total_count 0; that is still "no data", not a failure.
      const rows = response?.data === null && response?.total_count === 0 ? [] : response?.data;
      // An array (even an empty one) is a successful response; anything else
      // is a failure and must not be shown as "no data".
      if (Array.isArray(rows)) {
        // Process the bill detail data to match the format needed for PnL display
        const processedData = rows.map(item => ({
          trade_id: item.BillID,
          bill_id: item.BillID,
          trading_symbol: item.TradingSymbol,
          qty: Math.max(item.BuyQty || 0, item.SellQty || 0),
          buy_amount: item.BuyAmount,
          sell_amount: item.SellAmount,
          buy_avg: item.BuyRate,
          sell_avg: item.SellRate,
          realized_pnl: item.PNL,
          trade_time: item.CreatedAt,
          exchange: item.Exchange,
          narration: item.Narration
        }));

        setPnlHistory(processedData);
        setTotalCount(response.total_count || processedData.length);
      } else {
        throw new Error(response?.error || 'Failed to fetch PnL history');
      }
    } catch (error) {
      if (isSuperseded()) return;
      const message = toErrorMessage(error, 'Error fetching PnL history');
      console.error("Error fetching PnL history:", error);
      setPnlHistory([]);
      setTotalCount(0);
      setLoadError(message);
      enqueueSnackbar(message, {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 3000
      });
    } finally {
      if (!isSuperseded()) setLoading(false);
    }
  };

  // Initial load only; subsequent fetches are driven by filter/pagination handlers.
  useEffect(() => {
    fetchPnlData(page, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Leaving the tab supersedes whatever is still in flight, so a late failure
  // can't raise an error toast over the tab the user moved to.
  useEffect(() => () => { latestRequestIdRef.current += 1; }, []);

  // One request per date range: every day carries its per-exchange breakdown,
  // so switching exchange tabs re-derives the cells without refetching.
  useEffect(() => {
    // Ignore a response that arrives after the range has changed again.
    let isStale = false;
    // Old days would make the header sum the previous range until this lands.
    setCalendarDays([]);
    setCalendarLoading(true);
    setCalendarError(false);

    fetchPnlDaywise({
      start_date: formatApiDate(startDate),
      end_date: formatApiDate(endDate),
    })
      .then((response) => {
        if (isStale) return;
        // Go sends an empty list as null; the summary block marks a real response.
        const days = response?.days === null && response?.summary ? [] : response?.days;
        if (!Array.isArray(days)) throw new Error(response?.error || 'Failed to fetch PnL calendar');
        setCalendarDays(days);
      })
      .catch((error) => {
        if (isStale) return;
        console.error('Error fetching PnL calendar:', error);
        setCalendarDays([]);
        setCalendarError(true);
        enqueueSnackbar(toErrorMessage(error, 'Error fetching PnL calendar'), {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          autoHideDuration: 3000
        });
      })
      .finally(() => {
        if (!isStale) setCalendarLoading(false);
      });

    return () => { isStale = true; };
  }, [startDate, endDate, enqueueSnackbar]);

  const calendarData = useMemo(
    () => toDailyPnl(calendarDays, extraFilters.exchange),
    [calendarDays, extraFilters.exchange]
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchPnlData(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchPnlData(0, newRowsPerPage);
  };

  // Gross realised P&L for the whole range, or for the selected day — from the
  // daily totals, so it doesn't depend on which table page is showing.
  const summaryData = useMemo(() => {
    if (selectedDay) {
      const key = format(selectedDay, 'yyyy-MM-dd');
      return { totalRealizedPnL: calendarData.find((d) => d.date === key)?.pnl ?? 0 };
    }
    const total = calendarData.reduce((sum, d) => sum + d.pnl, 0);
    return { totalRealizedPnL: Math.round(total * 100) / 100 };
  }, [calendarData, selectedDay]);

  const pnlTotal = summaryData.totalRealizedPnL;
  const pnlTotalColor = pnlTotal > 0 ? theme.palette.success.main : pnlTotal < 0 ? theme.palette.error.main : theme.palette.text.secondary;

  // Shown in the pinned calendar header; the header shortens/hides the label
  // on narrow widths, the amount is never cut.
  const pnlSummary = {
    label: 'Gross Realised P&L : ',
    shortLabel: 'Gross P&L',
    // A skeleton while loading and '–' after a failed load, rather than ₹0.00
    // posing as the real total.
    value: calendarLoading ? (
      <Skeleton variant="text" sx={{ width: 72, fontSize: { xs: '0.8125rem', sm: '0.9rem' } }} />
    ) : calendarError ? (
      <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.8125rem', sm: '0.9rem' }, color: 'text.secondary' }}>
        –
      </Typography>
    ) : (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.8125rem', sm: '0.9rem' }, color: pnlTotalColor }}>
          {formatPnlInr(pnlTotal)}
        </Typography>
        {pnlTotal !== 0 && (pnlTotal > 0
          ? <ArrowDropUpIcon sx={{ color: pnlTotalColor, fontSize: 20, mr: -0.5 }} />
          : <ArrowDropDownIcon sx={{ color: pnlTotalColor, fontSize: 20, mr: -0.5 }} />)}
      </Box>
    ),
  };

  return (
    <ReportTabLayout>
      {/* Same layout as Trade History: presets on top, exchange sub-tabs below. */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 1,
          flexShrink: 0,
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '1px solid ' + theme.palette.divider,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <ReportsFilterBar
          filterProps={filterProps}
          showFilterDrawerButton={false}
          showActivePills={false}
          showPeriodChip={!isMobile}
          iconOnlyActions={iconOnlyActions}
          // Phones have no Reset; the presets row is the whole top row there.
          onReset={isMobile ? undefined : handleResetFilters}
          resetDisabled={loading}
        />
        {/* mb cancels the card's bottom padding so the tab underline sits on its edge. */}
        <Box sx={{ borderTop: 1, borderColor: 'divider', mb: -1.5 }}>
          <ReportsSubTabs
            ariaLabel="Exchange"
            options={EXCHANGE_OPTIONS}
            value={extraFilters.exchange}
            onChange={(exchange) => applyExtraFilter('exchange', exchange)}
          />
        </Box>
      </Paper>

      <CalendarTableSection
        containerId="pnl-history-table-container"
        startDate={startDate}
        endDate={endDate}
        data={calendarData}
        selectedDay={selectedDay}
        onDayClick={handleDayClick}
        onClearSelection={() => handleDayClick(selectedDay)}
        summary={pnlSummary}
      >
          {loading ? (
            isMobile ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              // Desktop keeps the header and fills the body with placeholder
              // rows, so the data drops in without the layout jumping.
              <PnlHistoryTable pnlHistory={[]} loading />
            )
          ) : loadError ? (
            <ReportsErrorState
              title="Couldn't Load P&L History"
              message={loadError}
              onRetry={() => fetchPnlData(page, rowsPerPage)}
              iconSize={48}
              padding={4}
            />
          ) : pnlHistory.length === 0 ? (
            <ReportsEmptyState
              icon={TimelineIcon}
              title="No P&L Data Available"
              message="No PnL data found for the selected date range."
              iconSize={48}
              padding={4}
            />
          ) : (
            <>
              {isMobile ? (
                // Mobile view - Simple Box layout without Cards
                <PnlHistoryMobileList pnlHistory={pnlHistory} />
              ) : (
                // Desktop view - Table layout 
                <PnlHistoryTable pnlHistory={pnlHistory} />
              )}
            </>
          )}
      </CalendarTableSection>

      <ReportsPagination
          show={pnlHistory.length > 0}
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
    </ReportTabLayout>
  );
};

export default PnlHistoryDetails;